import { WorkflowSourceEnum } from 'client/app/gql';
import * as FeatureToggles from 'common/features/featureTogglesForUI';
import {
  BundleParameters,
  Connection,
  DUMMY_REPOSITORIES,
  ElementInstance,
  Factors,
  Group,
  GroupMeta,
  ServerSideBundle,
  ServerSideElementInstance,
  Stage,
  TemplateWorkflow,
  WorkflowConfig,
} from 'common/types/bundle';
import { Schema } from 'common/types/schema';
import { SimulationError } from 'common/types/simulation_types';

// We are actively in the process of removing the types here and converging
// the representation of the workflow on the types in common/bundle.ts. That
// is to say, the V2 workflow data model. Once that is done we can delete
// this file.

export type ConnectionMaskDict = {
  // A map from input name to {source element instance name}.{output}
  [inputName: string]: string;
};

export type BuildBundleInput = {
  workflowMeta: ServerSideBundle['Meta'];
  config: WorkflowConfig;
  elementInstances: ElementInstance[];
  elementSetId: string;
  workflowSchemaVersion: string;
  InstancesConnections: Connection[];
  parameters: BundleParameters;
  template?: TemplateWorkflow;
  groups: Group[];
  stages: Stage[];
  factors: Factors | null;
  schema?: Schema;
  // workflow building does not need to concern itself with Design from
  // ServerSideBundle, which is the responsibility of visserver DOE Designer
};

// This function takes the client-side components representing a workflow
// and turns them into the structure of a workflow.
export function buildBundle(input: BuildBundleInput): Readonly<ServerSideBundle> {
  const {
    workflowMeta,
    config,
    elementInstances,
    InstancesConnections,
    parameters,
    groups,
    stages,
    factors,
    schema,
  } = input;
  const elementInstanceMap: {
    [name: string]: ServerSideElementInstance;
  } = elementInstances.reduce((acc, instance) => {
    const {
      Id,
      TypeName,
      Meta: { x, y, annotation },
    } = instance;

    const instanceParameters = parameters[instance.name];

    return {
      ...acc,
      [instance.name]: {
        Id,
        TypeName,
        // safeguard against: (4K screen + d3.js)-combo generates pixel
        // coordinates as floats (backend can't parse it)
        //
        // do not include calculated fields (e.g. outputs) stored in meta for
        // convenience. They can significantly increase bundle size
        Meta: {
          ...(annotation ? { annotation } : {}),
          x: Math.floor(x),
          y: Math.floor(y),
        },
        Parameters: instanceParameters || {},
      },
    };
  }, {});

  // We prioritise connections over input values. The backend will error if we
  // pass a bundle that contains a value for an input as well as a connection,
  // that the input is part of, so we remove any entries from the Parameters map
  // for inputs that are the target of a connection.
  InstancesConnections.forEach(connection => {
    const { ElementInstance: targetInstance, ParameterName: targetParameter } =
      connection.Target;

    if (elementInstanceMap?.[targetInstance]?.Parameters[targetParameter]) {
      delete elementInstanceMap[targetInstance].Parameters[targetParameter];
    }
  });

  // Create the connections explicitly. The connections passed to this function
  // can have extra props, such as the id, which we don't want to store
  // outside the client.
  const instancesConnections: Connection[] = InstancesConnections.map(conn => ({
    Source: {
      ElementInstance: conn.Source.ElementInstance,
      ParameterName: conn.Source.ParameterName,
    },
    Target: {
      ElementInstance: conn.Target.ElementInstance,
      ParameterName: conn.Target.ParameterName,
    },
  }));

  const Groups: Group[] = groups.map(group => {
    const groupMeta: GroupMeta = {
      x: group.Meta.x,
      y: group.Meta.y,
      width: group.Meta.width,
      height: group.Meta.height,
    };

    if (group.Meta.description) {
      groupMeta.description = group.Meta.description;
    }

    return {
      id: group.id,
      name: group.name,
      elementIds: group.elementIds,
      Meta: groupMeta,
    };
  });

  const Stages = stages?.map(stage => ({
    ...stage,
    meta: {
      x: stage.meta.x,
    },
  }));

  // because we haven't landed the backend yet, the element set many
  // still have a v2 version, but we're absolutely writing a v3 workflow
  let schemaVersion = input.workflowSchemaVersion;
  if (schemaVersion.startsWith('2.')) {
    schemaVersion = '3.0';
  }

  return {
    WorkflowId: '', // Empty for now. We could set this to the id of the workflow in Postgres later.
    Config: {
      ...config,
      global: {
        ...config.global,
      },
    },
    elementSetId: input.elementSetId,
    Meta: workflowMeta,
    Elements: {
      Instances: elementInstanceMap,
      InstancesConnections: instancesConnections,
    },
    SchemaVersion: schemaVersion,
    Repositories: DUMMY_REPOSITORIES,
    ...(input.template ? { Template: input.template } : {}),
    Groups,
    Stages,
    Schema: schema,
    Factors: factors ?? undefined,
    FeatureToggles: FeatureToggles.getAll() ?? undefined,
  };
}

// Workflow in format 2.0, antha-core.
// It is stored in datarepo for each job (more accurately, for latest
// successful simulation of that job).
// Schema:
// https://github.com/Synthace/antha/blob/master/workflow/schemas/workflow.schema.json
export type WorkflowFile = {
  // ... many more props we don't handle for now
  Simulation: {
    Elements: {
      Instances: {
        [id: string]: {
          // Element instance name, e.g. 'Aliquot DNA sample'
          Name: string;
          // Element type name, e.g. 'AliquotLiquid'
          TypeName: string;
          // Unique ID of this element instance
          Id?: string;
          // Id of the parent element. If this is present, it means this element
          // instance is *not* a real element instance in the workflow but was created
          // and called by a parent element on the fly.
          ParentId?: string;
          Files?: {
            // If this element produced files, the files are stored here.
            [elementParamName: string]:
              | WorkflowElementFile
              | null
              | readonly (WorkflowElementFile | null)[];
          };
        };
      };
    };
  };
};

// An element param can accept or produce files, which are stored in the
// datarepo. This represents one of those files.
export type WorkflowElementFile = {
  // User-defined name of the file
  Name: string;
  // If true, the file was produced by the element.
  // If false, the file was consumed by the element.
  IsOutput: boolean;
  // Path in the datarepo, relative to /job/<jobId>/simulation/data.
  // Example path: "1". Note that the path is auto-generated and does not
  // end with `Name`. This is because many elements can produce the same
  // file, and those all point to the same path "1". Think symlinks.
  Path: string;
};

export type SimulationStatus = 'IN_PROGRESS' | 'SUCCESS' | 'ERROR';

/**
 * Contains information needed to display a notification about a simulation.
 */
export type SimulationNotificationDetails = {
  name: string;
  requestID: string;
  status: SimulationStatus;
  startTime: string;
  /* Associated with the parent workflow of the snapshot of the simulation. */
  parentWorkflowId: string;
  /* Associated with the snapshot of the simulation. */
  workflowId?: string;
  source: WorkflowSourceEnum;

  simulationId?: string;

  errors?: readonly SimulationError[];
  logsUrl?: string | null;
  traceUrl?: string | null;

  isFavoritedByCurrentUser?: boolean;
  favoritedBy?: string[];

  cancelRequest?: () => void;
};
